import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  Grid,
  List,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';

import { ISelectedDataOption, ISelectedOption } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { getAuthState } from '../../../store/AuthSlice';
import {
  getCurriculums,
  getCurriculumState,
  resetSelectedValues,
  setSelectedValues,
} from '../../../store/CurriculumSlice';
import {
  checkUserPermission,
  constructOptionsFromArray,
} from '../../../helper';
import ImageContainer from './components/ImageContainer';
import LetterDivider from '../../../components/Divider/LetterDivider';
import CardWithSubjectCode from '../../../components/Card/CardWithSubjectCode';

interface ISortedListData {
  [key: string]: Array<ISelectedOption>;
}

interface QuestionBankProps {
  type: 'question' | 'quiz';
}

const allSelectedItem: ISelectedOption = { label: 'All', value: 'All' };
const emptySelectedItem: ISelectedOption = { label: '', value: '' };
const defaultSelectedDataOption: ISelectedDataOption = {
  selectedItem: emptySelectedItem,
  options: [],
};

const getSortedListData = (subjectOptions: ISelectedOption[] = []) => {
  const newSortedListData: ISortedListData = {};

  _.sortBy(subjectOptions, 'label').forEach((subjectOption) => {
    const letter = subjectOption.label.substr(0, 1).toUpperCase();

    if (newSortedListData[letter]) {
      newSortedListData[letter].push(subjectOption);
    } else {
      newSortedListData[letter] = [subjectOption];
    }
  });

  return newSortedListData;
};

const QuestionBank: React.FC<QuestionBankProps> = ({ type }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const curriculumState = useAppSelector(getCurriculumState);
  const authState = useAppSelector(getAuthState);

  // Permisisons
  const subTopicViewPermission = checkUserPermission(authState.user, {
    adminSubTopic: ['view'],
  });

  const quizViewPermission = checkUserPermission(authState.user, {
    adminQuiz: ['view'],
  });

  // State
  const [curriculumData, setCurriculumData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const [gradeData, setGradeData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const [subjectData, setSubjectData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const [sortedListData, setSortedListData] = useState<ISortedListData>({});

  useEffect(() => {
    dispatch(getCurriculums());
  }, []);

  useEffect(() => {
    onSubjectMapChange();
  }, [curriculumState.list]);

  const onSubjectMapChange = (data?: {
    curriculumOption?: ISelectedOption;
    gradeOption?: ISelectedOption;
    subjectOption?: ISelectedOption;
  }) => {
    const newCurriculumData: ISelectedDataOption = { ...curriculumData };
    const newGradeData: ISelectedDataOption = { ...gradeData };
    const newSubjectData: ISelectedDataOption = { ...subjectData };

    if (curriculumState.list && curriculumState.list.length) {
      newCurriculumData.options = constructOptionsFromArray(
        curriculumState.list,
        'name',
        'name'
      );

      if (data?.curriculumOption) {
        newCurriculumData.selectedItem = data.curriculumOption;
      } else if (curriculumState?.selectedValues?.curriculum) {
        newCurriculumData.selectedItem =
          curriculumState.selectedValues.curriculum;
      } else {
        // eslint-disable-next-line prefer-destructuring
        newCurriculumData.selectedItem = newCurriculumData.options[0];
      }

      const selectedCurriculum = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.name === newCurriculumData.selectedItem.label
      );

      if (
        selectedCurriculum &&
        selectedCurriculum.grades &&
        selectedCurriculum.grades.length
      ) {
        newGradeData.options = constructOptionsFromArray(
          selectedCurriculum.grades,
          'name',
          'name'
        );

        if (data?.gradeOption) {
          newGradeData.selectedItem = data.gradeOption;
        } else if (curriculumState?.selectedValues?.grade) {
          newGradeData.selectedItem = curriculumState.selectedValues.grade;
        } else {
          // eslint-disable-next-line prefer-destructuring
          newGradeData.selectedItem = newGradeData.options[0];
        }

        const selectedGrade = selectedCurriculum.grades.find(
          (gradeItem) => gradeItem.name === newGradeData.selectedItem.label
        );

        if (
          selectedGrade &&
          selectedGrade.subjects &&
          selectedGrade.subjects.length
        ) {
          newSubjectData.options = _.sortBy(
            constructOptionsFromArray(selectedGrade.subjects, 'name', 'code'),
            'label'
          );

          let newSortedListData: ISortedListData = {};

          if (data?.subjectOption) {
            newSubjectData.selectedItem = data.subjectOption;
          } else if (
            curriculumState?.selectedValues?.subject &&
            curriculumState?.selectedValues?.subject !== allSelectedItem
          ) {
            newSubjectData.selectedItem =
              curriculumState.selectedValues.subject;
            newSortedListData = getSortedListData([
              curriculumState.selectedValues.subject,
            ]);
          } else {
            newSubjectData.selectedItem = allSelectedItem;
            newSortedListData = getSortedListData(newSubjectData.options);
          }

          setSortedListData(newSortedListData);
        }
      }

      if (
        curriculumData?.selectedItem?.label &&
        gradeData?.selectedItem?.label &&
        subjectData?.selectedItem?.label &&
        (curriculumState?.selectedValues?.curriculum ||
          curriculumState?.selectedValues?.grade ||
          curriculumState?.selectedValues?.subject)
      ) {
        dispatch(resetSelectedValues());
      }
    }

    setCurriculumData(newCurriculumData);
    setGradeData(newGradeData);
    setSubjectData(newSubjectData);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    const newSubjectData: ISelectedDataOption = {
      ...subjectData,
      selectedItem: value,
    };

    let newSortedListData: ISortedListData = {};

    if (value === allSelectedItem) {
      newSortedListData = getSortedListData(newSubjectData.options);
    } else {
      newSortedListData = getSortedListData([value]);
    }

    setSortedListData(newSortedListData);
    setSubjectData(newSubjectData);
  };

  const isCurriculumListLoading = curriculumState.status === 'loading';

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      placeholder="Select"
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {isCurriculumListLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              params.InputProps.endAdornment
            )}
          </>
        ),
      }}
    />
  );

  const letters = Object.keys(sortedListData);

  return (
    <div>
      <ImageContainer type={type} />
      <Box style={{ padding: '0 30px' }}>
        <Grid container spacing={3} sx={{ mb: '30px' }}>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography>Curriculum</Typography>
            <Autocomplete
              fullWidth
              disableClearable
              size="small"
              loading={isCurriculumListLoading}
              options={curriculumData.options}
              value={curriculumData.selectedItem}
              renderInput={renderInput}
              onChange={(event, value) =>
                onSubjectMapChange({ curriculumOption: value })
              }
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography>Grade</Typography>
            <Autocomplete
              fullWidth
              disableClearable
              size="small"
              options={gradeData.options}
              loading={isCurriculumListLoading}
              value={gradeData.selectedItem}
              renderInput={renderInput}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              disabled={
                !curriculumData.selectedItem ||
                curriculumData.selectedItem === emptySelectedItem
              }
              onChange={(event, value) =>
                onSubjectMapChange({
                  curriculumOption: curriculumData.selectedItem,
                  gradeOption: value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography>Subject</Typography>
            <Autocomplete
              fullWidth
              disableClearable
              size="small"
              loading={isCurriculumListLoading}
              value={subjectData.selectedItem}
              renderInput={renderInput}
              onChange={handleSubjectChange}
              options={[allSelectedItem, ...subjectData.options]}
              disabled={
                !curriculumData.selectedItem ||
                curriculumData.selectedItem === emptySelectedItem ||
                !gradeData.selectedItem ||
                gradeData.selectedItem === emptySelectedItem
              }
            />
          </Grid>
        </Grid>
        <div>
          <List>
            {letters.map((letter, letterIndex) => (
              <div key={letterIndex}>
                <LetterDivider
                  text={letter}
                  containerProps={{ sx: { width: '95%' } }}
                />
                <Grid container spacing={2} sx={{ p: '15px 0' }}>
                  {sortedListData[letter].map((subjectOption, subjectIndex) => (
                    <Grid
                      key={subjectIndex}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2}
                    >
                      <CardWithSubjectCode
                        subject={subjectOption.label}
                        subjectCode={subjectOption.value}
                        disabled={
                          (type === 'question' && !subTopicViewPermission) ||
                          (type === 'quiz' && !quizViewPermission)
                        }
                        onClick={() => {
                          dispatch(
                            setSelectedValues({
                              curriculum: curriculumData.selectedItem,
                              grade: gradeData.selectedItem,
                              subject: subjectData.selectedItem,
                            })
                          );

                          if (subTopicViewPermission && type === 'question') {
                            navigate(
                              `${process.env.PUBLIC_URL}/dashboard/question-bank/sub-topics/${subjectOption.value}`
                            );
                          }
                          if (quizViewPermission && type === 'quiz') {
                            navigate(
                              `${process.env.PUBLIC_URL}/dashboard/quiz-bank/quizzes/${subjectOption.value}`
                            );
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            ))}
          </List>
        </div>
      </Box>
    </div>
  );
};

export default QuestionBank;
